import { Descriptions, Divider, Flex, Typography } from 'antd'
import type {
  Pill,
  PillCategories,
  PillInfo,
} from 'features/libra/dashboard/ticketDashboard/types'
import type { FC } from 'react'
import { Fragment, memo } from 'react'

import { LinkRow } from './LinkRow'

interface PillInfoListProps {
  pills: ReadonlyArray<Pill>
  categories: PillCategories
}

function infoTitle(pill: Pill, categories: PillCategories): string {
  return pill.info?.title ?? categories[pill.category]?.label ?? pill.category
}

function infoText(info: PillInfo | undefined): string {
  return [info?.valueDescription, info?.description].filter(Boolean).join(' · ')
}

/**
 * The explanation behind each pill. Pills with related items (e.g. comment
 * threads) get their own titled section instead.
 */
const RawPillInfoList: FC<PillInfoListProps> = ({ pills, categories }) => {
  const details = pills.filter((pill) => pill.info && !pill.info.items?.length)
  const lists = pills.filter((pill) => pill.info?.items?.length)

  return (
    <>
      {details.length > 0 && (
        <Descriptions
          bordered
          column={1}
          size='small'
          items={details.map((pill) => ({
            key: `${pill.category}:${pill.value}`,
            label: infoTitle(pill, categories),
            children: infoText(pill.info),
          }))}
        />
      )}
      {lists.map((pill) => (
        <Fragment key={`${pill.category}:${pill.value}`}>
          <Divider titlePlacement='start' plain>
            {infoTitle(pill, categories)} ({pill.info?.items?.length})
          </Divider>
          {infoText(pill.info) && (
            <Typography.Text type='secondary'>
              {infoText(pill.info)}
            </Typography.Text>
          )}
          <Flex vertical gap='small'>
            {pill.info?.items?.map((item) => (
              <LinkRow key={item.id} link={item.link}>
                <Typography.Text>{item.title}</Typography.Text>
              </LinkRow>
            ))}
          </Flex>
        </Fragment>
      ))}
    </>
  )
}

export const PillInfoList = memo(RawPillInfoList)
